import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import {
  Plus, X, Trash2, Calendar, Utensils, Activity, AlertTriangle, CheckCircle, Edit
} from '../components/Icons';
import { getStoolInfo } from '../utils/stoolInfo';
import styles from './GILog.module.css';
import BristolIllustration from '../components/BristolIllustration';

function initial(name = '') {
  return (name.trim()[0] || '?').toUpperCase();
}

// Sub-form satu waktu makan (pagi/siang/malam)
const MealSection = ({ mealKey, title, form, onChange, t }) => (
  <div className={styles.mealBox}>
    <div className={styles.mealTitle}>{title}</div>
    <div className={styles.grid3}>
      <div className={styles.field}>
        <label className={styles.label}>{t('log.appetite')}</label>
        <select className={styles.select} name={`appetite_${mealKey}`}
          value={form[`appetite_${mealKey}`]} onChange={onChange}>
          <option value="">-- Pilih --</option>
          <option value="Baik">Baik</option>
          <option value="Kurang">Kurang</option>
          <option value="Menolak">Menolak</option>
        </select>
      </div>
      <div className={styles.field}>
        <label className={styles.label}>{t('log.portion')}</label>
        <select className={styles.select} name={`portion_${mealKey}`}
          value={form[`portion_${mealKey}`]} onChange={onChange}>
          <option value="">-- Pilih --</option>
          <option value="Banyak">Banyak</option>
          <option value="Cukup">Cukup</option>
          <option value="Sedikit">Sedikit</option>
        </select>
      </div>
      <div className={styles.field}>
        <label className={styles.label}>{t('log.fluid')}</label>
        <select className={styles.select} name={`fluid_${mealKey}`}
          value={form[`fluid_${mealKey}`]} onChange={onChange}>
          <option value="">-- Pilih --</option>
          <option value="Cukup">Cukup</option>
          <option value="Kurang">Kurang</option>
        </select>
      </div>
    </div>
    <div className={styles.field}>
      <label className={styles.label}>{t('log.foodType')}</label>
      <input className={styles.input} type="text"
        name={`food_${mealKey}`} value={form[`food_${mealKey}`]}
        onChange={onChange} placeholder={t('log.foodPlaceholder')} />
    </div>
  </div>
);

function GILog() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isID = i18n.language === 'id';

  const [children, setChildren] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const emptyForm = {
    child_id: '', date: new Date().toISOString().split('T')[0],
    appetite_morning: '', food_morning: '', portion_morning: '', fluid_morning: '',
    appetite_noon: '', food_noon: '', portion_noon: '', fluid_noon: '',
    appetite_night: '', food_night: '', portion_night: '', fluid_night: '',
    stool_frequency: '', stool_type: '', stool_color: '',
    stool_smell: '', urination: '', stool_complaint: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchChildren = useCallback(async () => {
    const { data } = await supabase.from('children').select('id, name')
      .eq('caregiver_id', user.id);
    if (data) setChildren(data);
  }, [user.id]);

  const fetchLogs = useCallback(async () => {
    const { data } = await supabase.from('gi_logs')
      .select('*, children(name)')
      .eq('caregiver_id', user.id)
      .order('date', { ascending: false })
      .limit(20);
    if (data) setLogs(data);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchChildren(); fetchLogs(); }, [fetchChildren, fetchLogs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // ===== FIX BUG #8: gagal simpan tidak lagi diam-diam =====
  // Ubah string kosong "" jadi null (database menolak "" pada kolom ber-constraint)
  const cleanPayload = (f) => {
    const cleaned = {};
    Object.keys(f).forEach(key => {
      const val = f[key];
      cleaned[key] = (val === '' || val === undefined) ? null : val;
    });
    return cleaned;
  };

  const [editingId, setEditingId] = useState(null);

  const startEdit = (log) => {
    setForm({
      child_id: log.child_id || '',
      date: log.date || '',
      appetite_morning: log.appetite_morning || '', food_morning: log.food_morning || '',
      portion_morning: log.portion_morning || '', fluid_morning: log.fluid_morning || '',
      appetite_noon: log.appetite_noon || '', food_noon: log.food_noon || '',
      portion_noon: log.portion_noon || '', fluid_noon: log.fluid_noon || '',
      appetite_night: log.appetite_night || '', food_night: log.food_night || '',
      portion_night: log.portion_night || '', fluid_night: log.fluid_night || '',
      stool_frequency: log.stool_frequency ?? '', stool_type: log.stool_type ?? '',
      stool_color: log.stool_color || '', stool_smell: log.stool_smell || '',
      urination: log.urination || '', stool_complaint: log.stool_complaint || '',
    });
    setEditingId(log.id);
    setShowForm(true);
    setErrorMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    // Validasi field wajib: anak & tanggal
    if (!form.child_id) {
      setErrorMsg(isID ? 'Mohon pilih anak terlebih dahulu.' : 'Please select a child first.');
      return;
    }
    if (!form.date) {
      setErrorMsg(isID ? 'Mohon isi tanggal catatan.' : 'Please fill in the date.');
      return;
    }

    setSaving(true);
    const payload = cleanPayload(form);
    const finalPayload = {
      ...payload,
      caregiver_id: user.id,
      stool_type: form.stool_type ? parseInt(form.stool_type) : null,
      stool_frequency: form.stool_frequency ? parseInt(form.stool_frequency) : null,
    };

    let error;
    if (editingId) {
      ({ error } = await supabase.from('gi_logs').update(finalPayload).eq('id', editingId));
    } else {
      ({ error } = await supabase.from('gi_logs').insert([finalPayload]));
    }
    setSaving(false);

    if (error) {
      setErrorMsg(
        isID
          ? `Gagal menyimpan catatan. ${error.message}. Data Anda masih tersimpan di formulir, silakan coba lagi.`
          : `Failed to save record. ${error.message}. Your data is still in the form, please try again.`
      );
      return;
    }

    setSuccessMsg(editingId
      ? (isID ? 'Catatan berhasil diperbarui.' : 'Record updated successfully.')
      : t('log.saveSuccess'));
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    fetchLogs();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleDeleteLog = async (id) => {
    const ok = window.confirm(isID
      ? 'Apakah Anda yakin ingin menghapus catatan ini?'
      : 'Are you sure you want to delete this record?');
    if (!ok) return;

    const { error } = await supabase.from('gi_logs').delete().eq('id', id);
    if (error) {
      setErrorMsg(isID
        ? `Gagal menghapus: ${error.message}`
        : `Failed to delete: ${error.message}`);
      return;
    }
    setSuccessMsg(isID ? 'Catatan berhasil dihapus.' : 'Record deleted successfully.');
    fetchLogs();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // Tipe feses untuk dropdown — istilah awam + penjelasan
  const stoolTypes = [1, 2, 3, 4, 5, 6, 7].map(v => ({
    value: v,
    label: t(`log.stool${v}`),
  }));

  const badgeClass = (val, goodVal) =>
    val === goodVal ? styles.badgeGood : styles.badgeBad;

  const toneCls = (tone) =>
    tone === 'danger' ? styles.toneDanger : tone === 'warn' ? styles.toneWarn : styles.toneGood;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <Activity size={24} aria-hidden="true" />
            {t('nav.log')}
          </h1>
          <p className={styles.subtitle}>{t('log.subtitle')}</p>
        </div>
        <button className={styles.addBtn} onClick={() => {
          if (showForm) { setEditingId(null); setForm(emptyForm); }
          setShowForm(!showForm); setErrorMsg('');
        }}>
          {showForm
            ? <><X size={15} aria-hidden="true" /> {t('common.cancel')}</>
            : <><Plus size={15} aria-hidden="true" /> {t('log.addLog')}</>}
        </button>
      </div>

      {successMsg && (
        <div className={styles.successMsg}>
          <CheckCircle size={16} aria-hidden="true" />{successMsg}
        </div>
      )}
      {errorMsg && (
        <div className={styles.errorMsg}>
          <AlertTriangle size={18} aria-hidden="true" />{errorMsg}
        </div>
      )}
      {children.length === 0 && (
        <div className={styles.warningBox}>
          <AlertTriangle size={16} aria-hidden="true" />{t('log.noChild')}
        </div>
      )}

      {/* ===== FORM ===== */}
      {showForm && (
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>
            <Plus size={20} aria-hidden="true" />
            {editingId ? (isID ? 'Edit Catatan' : 'Edit Record') : t('log.addLog')}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className={styles.sectionLabel}>{t('log.childAndDate')}</div>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label}>{t('log.selectChild')}</label>
                <select className={styles.select} name="child_id"
                  value={form.child_id} onChange={handleChange} required>
                  <option value="">{t('log.selectChildPlaceholder')}</option>
                  {children.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{t('log.date')}</label>
                <input className={styles.input} type="date" name="date"
                  value={form.date} onChange={handleChange} required />
              </div>
            </div>

            <div className={styles.sectionLabel}>
              <Utensils size={14} aria-hidden="true" />{t('log.mealSection')}
            </div>
            <MealSection mealKey="morning" title={t('log.morning')} form={form} onChange={handleChange} t={t} />
            <MealSection mealKey="noon" title={t('log.noon')} form={form} onChange={handleChange} t={t} />
            <MealSection mealKey="night" title={t('log.night')} form={form} onChange={handleChange} t={t} />

            <div className={styles.sectionLabel}>
              <Activity size={14} aria-hidden="true" />{t('log.babSection')}
            </div>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label}>{t('log.frequency')} (x/hari)</label>
                <input className={styles.input} type="number" name="stool_frequency"
                  min="0" max="20" value={form.stool_frequency}
                  onChange={handleChange} placeholder="0" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  {isID ? 'Bentuk Feses (Tinja)' : 'Stool Form'}{' '}
                  <span className={styles.labelSub}>({isID ? 'Skala Bristol' : 'Bristol Scale'})</span>
                </label>
                <select className={styles.select} name="stool_type"
                  value={form.stool_type} onChange={handleChange}>
                  <option value="">-- Pilih --</option>
                  {stoolTypes.map(s => (
                    <option key={s.value} value={s.value}>
                      {isID ? 'Tipe' : 'Type'} {s.value} — {s.label}
                    </option>
                  ))}
                </select>
              </div>
              {form.stool_type && (
                <div className={styles.bristolPreview}>
                  <BristolIllustration type={form.stool_type} size={96} />
                  <div className={styles.bristolPreviewText}>
                    <div className={styles.bristolPreviewTitle}>
                      {isID ? 'Tipe' : 'Type'} {form.stool_type}
                    </div>
                    <div className={styles.bristolPreviewDesc}>
                      {getStoolInfo(form.stool_type, isID)?.label}
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.field}>
                <label className={styles.label}>{t('log.stoolColor')}</label>
                <select className={styles.select} name="stool_color"
                  value={form.stool_color} onChange={handleChange}>
                  <option value="">-- Pilih --</option>
                  <option value="Kuning">Kuning</option>
                  <option value="Coklat">Coklat</option>
                  <option value="Hitam">Hitam</option>
                  <option value="Pucat">Pucat</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{t('log.stoolSmell')}</label>
                <select className={styles.select} name="stool_smell"
                  value={form.stool_smell} onChange={handleChange}>
                  <option value="">-- Pilih --</option>
                  <option value="Normal">Normal</option>
                  <option value="Menyengat">Menyengat</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{t('log.urination')}</label>
                <select className={styles.select} name="urination"
                  value={form.urination} onChange={handleChange}>
                  <option value="">-- Pilih --</option>
                  <option value="Lancar">Lancar</option>
                  <option value="Tidak Lancar">Tidak Lancar</option>
                </select>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('log.stoolComplaint')}</label>
              <textarea className={styles.textarea} name="stool_complaint"
                value={form.stool_complaint} onChange={handleChange}
                placeholder={t('log.complaintPlaceholder')} rows={3} />
            </div>

            <button type="submit" className={styles.submitBtn} disabled={saving}>
              {saving
                ? t('common.loading')
                : <><CheckCircle size={18} aria-hidden="true" />{t('common.save')}</>}
            </button>
          </form>
        </div>
      )}

      {/* ===== RIWAYAT ===== */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Calendar size={20} aria-hidden="true" />{t('log.history')}
        </h2>
        {loading ? (
          <p className={styles.empty}>{t('common.loading')}</p>
        ) : logs.length === 0 ? (
          <p className={styles.empty}>{t('log.noLogs')}</p>
        ) : (
          logs.map(log => {
            const info = log.stool_type ? getStoolInfo(log.stool_type, isID) : null;
            return (
              <div key={log.id} className={styles.logCard}>
                <div className={styles.logHeader}>
                  <span className={styles.logDate}>
                    <Calendar size={14} aria-hidden="true" />{log.date}
                  </span>
                  <div className={styles.logHeaderRight}>
                    {log.children && (
                      <span className={styles.childTag}>
                        <span className={styles.childTagAvatar}>{initial(log.children.name)}</span>
                        {log.children.name}
                      </span>
                    )}
                    <button className={styles.editBtn} onClick={() => startEdit(log)}>
                      <Edit size={13} aria-hidden="true" />{isID ? 'Edit' : 'Edit'}
                    </button>
                    <button className={styles.deleteBtn} onClick={() => handleDeleteLog(log.id)}>
                      <Trash2 size={13} aria-hidden="true" />{isID ? 'Hapus' : 'Delete'}
                    </button>
                  </div>
                </div>

                <div className={styles.logSubhead}>
                  <Utensils size={13} aria-hidden="true" />{isID ? 'Asupan' : 'Intake'}
                </div>
                <div className={styles.logGrid3}>
                  {['morning', 'noon', 'night'].map(m => (
                    <div key={m} className={styles.mealSummary}>
                      <div className={styles.mealSummaryTitle}>
                        {m === 'morning' ? (isID ? 'Pagi' : 'Morning')
                          : m === 'noon' ? (isID ? 'Siang' : 'Noon')
                          : (isID ? 'Malam' : 'Night')}
                      </div>
                      <div className={styles.logMini}>{isID ? 'Nafsu' : 'Appetite'}: {log[`appetite_${m}`] || '-'}</div>
                      <div className={styles.logMini}>{isID ? 'Porsi' : 'Portion'}: {log[`portion_${m}`] || '-'}</div>
                      <div className={styles.logMini}>{isID ? 'Cairan' : 'Fluid'}: {log[`fluid_${m}`] || '-'}</div>
                      {log[`food_${m}`] && <div className={styles.logMiniFood}>{log[`food_${m}`]}</div>}
                    </div>
                  ))}
                </div>

                <div className={styles.logSubhead}>
                  <Activity size={13} aria-hidden="true" />{isID ? 'Kondisi BAB' : 'Bowel Condition'}
                </div>
                <div className={styles.logGrid}>
                  <div className={styles.logItem}>
                    <span className={styles.logItemLabel}>{t('log.frequency')}</span>
                    <span className={styles.logValue}>{log.stool_frequency ?? '-'}x</span>
                  </div>
                  <div className={styles.logItem}>
                    <span className={styles.logItemLabel}>{isID ? 'Bentuk Feses (Tinja)' : 'Stool Form'}</span>
                    <span className={styles.logValue}>{isID ? 'Tipe' : 'Type'} {log.stool_type ?? '-'}</span>
                    {info && <span className={`${styles.logNote} ${toneCls(info.tone)}`}>{info.label}</span>}
                  </div>
                  <div className={styles.logItem}>
                    <span className={styles.logItemLabel}>{t('log.stoolColor')}</span>
                    <span className={styles.logValue}>{log.stool_color || '-'}</span>
                  </div>
                  <div className={styles.logItem}>
                    <span className={styles.logItemLabel}>{t('log.stoolSmell')}</span>
                    <span className={`${styles.badge} ${log.stool_smell ? badgeClass(log.stool_smell, 'Normal') : ''}`}>
                      {log.stool_smell || '-'}
                    </span>
                  </div>
                  <div className={styles.logItem}>
                    <span className={styles.logItemLabel}>{t('log.urination')}</span>
                    <span className={`${styles.badge} ${log.urination ? badgeClass(log.urination, 'Lancar') : ''}`}>
                      {log.urination || '-'}
                    </span>
                  </div>
                </div>

                {log.stool_complaint && (
                  <div className={styles.logNotes}>
                    <AlertTriangle size={14} aria-hidden="true" />{log.stool_complaint}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default GILog;